import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaDollarSign, FaFutbol, FaMapMarkerAlt } from 'react-icons/fa';
import { AppHeader } from './AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { useMatchPreview } from '../hooks/useMatchPreview';
import { useResponsive } from '../hooks/useResponsive';
import { theme, alpha } from '../theme';

const FOMO_THRESHOLD = 5;

const Icon = (C: any) => C as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
const CalIcon = Icon(FaCalendarAlt);
const ClockIcon = Icon(FaClock);
const PriceIcon = Icon(FaDollarSign);
const BallIcon = Icon(FaFutbol);
const PinIcon = Icon(FaMapMarkerAlt);

function getFomoCopy(spotsLeft: number): string {
  if (spotsLeft <= 1) return 'ÚLTIMO LUGAR DISPONIBLE';
  if (spotsLeft === 2) return 'Solo 2 lugares';
  return `Quedan ${spotsLeft} lugares`;
}

// Mapeo de status raw del server a labels humanos en español.
// Si llega un status que no conocemos, lo mostramos tal cual (capitalizado).
function getStatusLabel(status: string): string {
  const map: Record<string, string> = {
    SCHEDULED: 'Confirmado',
    OPEN: 'Abierto',
    IN_PROGRESS: 'En curso',
    FINISHED: 'Finalizado',
    CANCELLED: 'Cancelado',
    COMPLETED: 'Finalizado',
  };
  if (map[status]) return map[status];
  if (!status) return '';
  // Fallback: capitalize first letter, rest lowercase
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

const MatchPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { match, loading, error } = useMatchPreview(id);

  const handleAction = () => {
    if (!id) return;
    if (user) {
      navigate(`/match/${id}`);
      return;
    }
    try {
      localStorage.setItem('pendingMatchJoin', id);
    } catch {
      // ignore storage failures
    }
    navigate('/auth');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '80px 16px 100px' : '20px 40px 100px',
      }}
    >
      <AppHeader title="Vista previa del partido" showBackButton />

      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {loading && (
          <div
            style={{
              background: theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 16,
              padding: 40,
              textAlign: 'center',
              color: theme.colors.textSecondary,
            }}
          >
            Cargando partido…
          </div>
        )}

        {!loading && (error || !match) && (
          <div
            style={{
              background: theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 16,
              padding: 40,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
            <div style={{ fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
              No pudimos cargar el partido
            </div>
            <div style={{ color: theme.colors.textSecondary, fontSize: 14 }}>
              {error || 'Probá de nuevo en unos segundos.'}
            </div>
          </div>
        )}

        {!loading && match && (
          <>
            <div
              style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 16,
                padding: 24,
                marginBottom: 16,
              }}
            >
              <h1
                style={{
                  fontFamily: theme.fonts.heading,
                  color: theme.colors.text,
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 800,
                }}
              >
                {match.location}
              </h1>
              <div
                data-testid="match-preview-status-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: alpha('primary', '20'),
                  color: theme.colors.primary,
                  fontSize: 12,
                  fontWeight: 700,
                  marginTop: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {getStatusLabel(match.status)}
              </div>

              <div
                style={{
                  marginTop: 20,
                  padding: 16,
                  borderRadius: 12,
                  background: match.isFull
                    ? alpha('danger', '20')
                    : match.isAlmostFull
                      ? alpha('warning', '20')
                      : alpha('primary', '15'),
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: match.isFull
                      ? theme.colors.danger
                      : match.isAlmostFull
                        ? theme.colors.warning
                        : theme.colors.primary,
                    fontFamily: theme.fonts.heading,
                  }}
                >
                  {match.currentPlayers}/{match.maxTotalPlayers}
                </div>
                <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 }}>
                  jugadores confirmados
                </div>
              </div>
            </div>

            {match.spotsLeft > 0 && match.spotsLeft <= FOMO_THRESHOLD && !match.isFull && (
              <div
                style={{
                  background: theme.colors.warning,
                  color: theme.colors.background,
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 16,
                  textAlign: 'center',
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  fontFamily: theme.fonts.heading,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                }}
              >
                ⚡ {getFomoCopy(match.spotsLeft)}
              </div>
            )}

            <div
              style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 16,
                padding: 20,
                marginBottom: 16,
              }}
            >
              <h2
                style={{
                  fontFamily: theme.fonts.heading,
                  color: theme.colors.text,
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Información del partido
              </h2>

              <Row icon={<CalIcon size={18} />} text={match.date} />
              <Row icon={<ClockIcon size={18} />} text={match.time} />
              <Row icon={<PriceIcon size={18} />} text={`$${match.price}`} />
              <Row icon={<BallIcon size={18} />} text={`Formato ${match.maxPlayers}v${match.maxPlayers}`} />
              {match.mapsUrl ? (
                <a
                  href={match.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Row
                    icon={<PinIcon size={18} style={{ color: theme.colors.primary }} />}
                    text={`${match.location} (ver mapa)`}
                    linkLike
                  />
                </a>
              ) : (
                <Row icon={<PinIcon size={18} />} text={match.location} />
              )}
            </div>

            <div
              style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 16,
                padding: 20,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  background: theme.colors.primary,
                  color: theme.colors.background,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: theme.fonts.heading,
                  fontSize: 16,
                }}
              >
                {(match.creator.name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>Organizado por</div>
                <div style={{ fontWeight: 700, color: theme.colors.text }}>{match.creator.name}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && match && (() => {
        // Partidos privados (no `isPublic`) llegan por invite link de WhatsApp;
        // el user solo se anota, no "postula". Partidos públicos siguen el
        // flujo de discover con postulación al organizador.
        const isInvite = !match.isPublic;

        const ctaLabel = user
          ? match.isFull
            ? isInvite
              ? 'Sumarme como suplente'
              : 'Ver partido y postularme'
            : isInvite
              ? 'Me Sumo al partido'
              : 'Postularme al partido'
          : match.isFull
            ? 'Registrate y sumate como suplente'
            : isInvite
              ? 'Registrate y sumate'
              : 'Registrate y postulate';

        const subtitle = user
          ? isInvite
            ? 'Tu nombre aparece en la lista al toque.'
            : 'Tu solicitud va al organizador para confirmar.'
          : isInvite
            ? 'Necesitás una cuenta para sumarte al partido.'
            : 'Necesitás una cuenta para postularte al partido.';

        return (
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: theme.colors.surface,
              borderTop: `1px solid ${theme.colors.border}`,
              padding: '14px 20px 20px',
              zIndex: 50,
            }}
          >
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
              <button
                type="button"
                onClick={handleAction}
                data-testid="match-preview-cta"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: theme.colors.primary,
                  color: theme.colors.background,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: theme.fonts.heading,
                  letterSpacing: 0.5,
                }}
              >
                {ctaLabel}
              </button>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  color: theme.colors.textSecondary,
                  marginTop: 6,
                }}
              >
                {subtitle}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const Row: React.FC<{ icon: React.ReactNode; text: string; linkLike?: boolean }> = ({
  icon,
  text,
  linkLike,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
    <span style={{ color: theme.colors.textSecondary, display: 'flex' }}>{icon}</span>
    <span
      style={{
        color: linkLike ? theme.colors.primary : theme.colors.text,
        fontSize: 14,
        fontWeight: linkLike ? 600 : 400,
      }}
    >
      {text}
    </span>
  </div>
);

export default MatchPreviewPage;
